import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import QuotaBlockedNotice from '../../components/subscription/QuotaBlockedNotice';
import { parseFormattedMoneyToCents, formatCurrencyInCents } from '../../helpers/formatters';
import {
    OPERATIONAL_COST_CATEGORIES,
    OPERATIONAL_COST_FREQUENCIES,
    OPERATIONAL_COST_ONE_TIME,
    OPERATIONAL_COST_RECURRING,
    isRecurringOperationalCost,
    operationalCostMonthlyCents,
    operationalCostQuotaKey,
} from '../../helpers/operationalCosts';
import { invalidatePlanUsage } from '../../helpers/planUsage';

function OperationalCostForm({ isEdit }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [recurrence, setRecurrence] = useState(OPERATIONAL_COST_RECURRING);
    const [frequency, setFrequency] = useState('monthly');
    const [notes, setNotes] = useState('');
    const [amount, setAmount] = useState('');
    const [amountFloat, setAmountFloat] = useState(undefined);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({ has_errors: false });
    const [duplicateWarning, setDuplicateWarning] = useState('');

    useEffect(() => {
        if (!isEdit || !id) {
            return undefined;
        }
        setIsLoading(true);
        api.get(`operational-costs/${id}`)
            .then((response) => {
                const data = response.data.data || {};
                setName(data.name || '');
                setCategory(data.category || '');
                setRecurrence(data.recurrence === OPERATIONAL_COST_ONE_TIME
                    ? OPERATIONAL_COST_ONE_TIME
                    : OPERATIONAL_COST_RECURRING);
                setFrequency(data.frequency || 'monthly');
                setNotes(data.notes || '');
                if (typeof data.amount_cents === 'number') {
                    const reais = data.amount_cents / 100;
                    setAmount(String(reais));
                    setAmountFloat(reais);
                }
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
        return undefined;
    }, [id, isEdit]);

    useEffect(() => {
        const trimmedName = name.trim();
        if (!trimmedName || !category) {
            setDuplicateWarning('');
            return undefined;
        }
        const timer = setTimeout(() => {
            api.get('operational-costs', {
                params: {
                    page: 1,
                    limit: 20,
                    category,
                    q: trimmedName,
                },
            })
                .then((response) => {
                    const items = response.data?.data?.items || [];
                    const match = items.find((item) => {
                        if (isEdit && (item.id === id || item.uuid === id)) {
                            return false;
                        }
                        return String(item.name || '').trim().toLowerCase() === trimmedName.toLowerCase()
                            && String(item.category || '') === category;
                    });
                    setDuplicateWarning(match
                        ? `Já existe um custo ativo com o nome "${match.name}" na categoria ${OPERATIONAL_COST_CATEGORIES.find((c) => c.value === category)?.label || category}.`
                        : '');
                })
                .catch(() => {
                    setDuplicateWarning('');
                });
        }, 400);
        return () => clearTimeout(timer);
    }, [name, category, id, isEdit]);

    const amountCents = useMemo(
        () => parseFormattedMoneyToCents(amountFloat ?? amount) || 0,
        [amount, amountFloat],
    );
    const monthlyCents = useMemo(
        () => (isRecurringOperationalCost(recurrence)
            ? operationalCostMonthlyCents(amountCents, frequency)
            : amountCents),
        [amountCents, frequency, recurrence],
    );
    const showProrate = isRecurringOperationalCost(recurrence) && frequency !== 'monthly' && amountCents > 0;

    const renderFieldError = (field) => {
        if (!resultErrors[field]) {
            return null;
        }
        return (
            <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {resultErrors[field]}
            </div>
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const nextErrors = { has_errors: false };
        if (!name.trim()) {
            nextErrors.name = 'Informe o nome do custo.';
            nextErrors.has_errors = true;
        }
        if (!category) {
            nextErrors.category = 'Selecione a categoria.';
            nextErrors.has_errors = true;
        }
        if (amountCents <= 0) {
            nextErrors.amount_cents = 'Informe um valor maior que zero.';
            nextErrors.has_errors = true;
        }
        if (nextErrors.has_errors) {
            setResultErrors(nextErrors);
            return;
        }
        if (isSubmitting) {
            return;
        }

        const payload = {
            name: name.trim(),
            category,
            amount_cents: amountCents,
            recurrence,
            frequency: isRecurringOperationalCost(recurrence) ? frequency : 'monthly',
            notes: notes.trim(),
        };

        setIsSubmitting(true);
        setResultErrors({ has_errors: false });
        const request = isEdit
            ? api.put(`operational-costs/${id}`, payload)
            : api.post('operational-costs', payload);

        request
            .then(() => {
                invalidatePlanUsage();
                navigate('/admin/operational-costs');
            })
            .catch((error) => {
                const errors = error?.response?.data?.errors || {};
                if (errors.form) {
                    setResultErrors({ has_errors: true, form: errors.form });
                } else {
                    setResultErrors({
                        has_errors: true,
                        form: error?.response?.data?.message || 'Não foi possível salvar o custo.',
                    });
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/operational-costs')} to="/admin/operational-costs">
                    Custos operacionais
                </Link>
                <Typography color="text.primary">{isEdit ? 'Editar' : 'Novo'}</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>
                {isEdit ? 'Editar custo operacional' : 'Novo custo operacional'}
            </Typography>

            {isEdit ? null : (
                <QuotaBlockedNotice
                    quotaKey={operationalCostQuotaKey(recurrence)}
                    backTo="/admin/operational-costs"
                />
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, maxWidth: 720 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Nome"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Ex.: Aluguel do galpão"
                            required
                        />
                        {renderFieldError('name')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required error={Boolean(resultErrors.category)}>
                            <InputLabel id="category-label">Categoria</InputLabel>
                            <Select
                                labelId="category-label"
                                label="Categoria"
                                value={category}
                                onChange={(event) => setCategory(event.target.value)}
                            >
                                {OPERATIONAL_COST_CATEGORIES.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderFieldError('category')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            customInput={TextField}
                            fullWidth
                            required
                            label="Valor"
                            value={amount}
                            onValueChange={(values) => {
                                setAmount(values.value);
                                setAmountFloat(values.floatValue);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="R$ "
                            allowNegative={false}
                            error={Boolean(resultErrors.amount_cents)}
                            helperText={resultErrors.amount_cents || undefined}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                            Tipo
                        </Typography>
                        <RadioGroup
                            row
                            value={recurrence}
                            onChange={(event) => setRecurrence(event.target.value)}
                        >
                            <FormControlLabel
                                value={OPERATIONAL_COST_RECURRING}
                                control={<Radio />}
                                label="Recorrente"
                            />
                            <FormControlLabel
                                value={OPERATIONAL_COST_ONE_TIME}
                                control={<Radio />}
                                label="Pontual"
                            />
                        </RadioGroup>
                        <FormHelperText sx={{ mx: 0, mt: 0.5 }}>
                            Recorrente = despesa que se repete (aluguel, assinatura, IPTU).
                            Pontual = gasto avulso deste período. Arquivar libera espaço no limite do plano;
                            desarquivar só funciona se ainda houver cota.
                        </FormHelperText>
                    </Grid>
                    {isRecurringOperationalCost(recurrence) ? (
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="frequency-label">Frequência</InputLabel>
                                <Select
                                    labelId="frequency-label"
                                    label="Frequência"
                                    value={frequency}
                                    onChange={(event) => setFrequency(event.target.value)}
                                >
                                    {OPERATIONAL_COST_FREQUENCIES.map((item) => (
                                        <MenuItem key={item.value} value={item.value}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    Para períodos maiores que 1 mês, o valor cheio é guardado e o impacto mensal é rateado.
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    ) : null}
                    {showProrate ? (
                        <Grid item xs={12}>
                            <Alert severity="info">
                                Valor cadastrado: {formatCurrencyInCents(amountCents)}.
                                Impacto mensal rateado: {formatCurrencyInCents(monthlyCents)}.
                            </Alert>
                        </Grid>
                    ) : null}
                    {duplicateWarning ? (
                        <Grid item xs={12}>
                            <Alert severity="warning">{duplicateWarning}</Alert>
                        </Grid>
                    ) : null}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Notas"
                            value={notes}
                            onChange={(event) => setNotes(event.target.value)}
                            placeholder="Opcional"
                        />
                    </Grid>
                </Grid>

                {renderFieldError('form')}

                <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="inherit"
                        disabled={isSubmitting}
                        onClick={() => navigate('/admin/operational-costs')}
                    >
                        Cancelar
                    </Button>
                </Box>
            </Box>
        </div>
    );
}

export function RegisterOperationalCostPage() {
    return <OperationalCostForm isEdit={false} />;
}

export function EditOperationalCostPage() {
    return <OperationalCostForm isEdit />;
}

export default OperationalCostForm;
